<!--
	Legend panel for the SafeSystem Identify tab: system layer, Crashes, Upload crash data.
-->
<script lang="ts">
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import LegendRow from '#lib/components/LegendRow.svelte';
	import InfoButton from '#lib/components/InfoButton.svelte';
	import LegendSection from '#lib/components/LegendSection.svelte';
	import DataNotAvailable from '#lib/components/DataNotAvailable.svelte';
	import CustomCrashes from '#lib/components/map/overlays/CustomCrashes.svelte';
	import LegendItems from './LegendItems.svelte';
	import { legendStore } from '#lib/stores/legendStore.svelte.js';
	import { geodataStore } from '#lib/stores/geodataStore.svelte.js';
	import { mapExtentStore } from '#lib/stores/mapExtentStore.svelte.js';
	import {
		ECrashesModule,
		ECrashes,
		type ICrashesSystemLayers
	} from '#lib/apps/safesystem/context/interfaces.js';
	import { disableByZoom } from '../legendHelpers';

	interface Props {
		heading: string;
		/** Crash year range shown when the layer carries no `detail`. */
		crashesFallback?: string;
		/** Hide the upload section when crash data is absent. */
		uploadNeedsCrashes?: boolean;
	}
	const { heading, crashesFallback, uploadNeedsCrashes = false }: Props = $props();

	// geodataStore.systemData is `unknown` at the store level; both apps key Identify as 'Identify'.
	const systemData = $derived(
		geodataStore.systemData as ICrashesSystemLayers & { Identify?: { Identify?: unknown } }
	);

	// A section is "available" when getVectorLayerSource() emitted a non-null ILayerSource for it.
	const identifyAvailable = $derived(systemData.Identify?.Identify != null);
	const crashesCircle = $derived(systemData[ECrashesModule.Crashes]?.[ECrashes.CrashesCircle]);
	const crashesAvailable = $derived(crashesCircle != null);

	const identifyLegend = $derived(legendStore.layers.find((l) => l.systemId === 'Identify'));
	const crashesCircleLegend = $derived(
		legendStore.layers.find((l) => l.systemId === ECrashes.CrashesCircle)
	);
	const crashesHeatmapLegend = $derived(
		legendStore.layers.find((l) => l.systemId === ECrashes.CrashesHeatmap)
	);

	const mapZoom = $derived(mapExtentStore.zoom);

	function toggleCrashesVisibility() {
		if (!crashesCircleLegend || !crashesHeatmapLegend) return;
		const enabled = !crashesCircleLegend.layerAllEnabled;
		for (const l of [crashesCircleLegend, crashesHeatmapLegend]) {
			legendStore.setLegendLayerVisibility({
				layerId: l.layerId,
				legendItemId: l.legendItems[0].legendItemId,
				enabled
			});
		}
	}
</script>

<div class="legendBox">
	<LegendSection {heading}>
		{#if !identifyAvailable || !identifyLegend}
			<DataNotAvailable />
		{:else}
			<LegendItems
				legend={identifyLegend}
				patch="round"
				disabled={disableByZoom(identifyLegend.minzoom, mapZoom)} />
		{/if}
	</LegendSection>

	<LegendSection heading="Crashes">
		{#snippet action()}
			{#if crashesCircle?.abstract}
				<InfoButton content={crashesCircle.abstract} />
			{/if}
		{/snippet}
		{#if !crashesAvailable || !crashesCircleLegend || !crashesHeatmapLegend}
			<DataNotAvailable />
		{:else}
			{@const detail = crashesCircle?.detail ?? crashesFallback}
			<div class="legendRowBox">
				{#snippet crashesLabel()}
					Fatal crash
					{#if detail}<small class="detail">{detail}</small>{/if}
				{/snippet}
				{#if disableByZoom(crashesHeatmapLegend.minzoom, mapZoom)}
					<p class="hint">Zoom in to enable this layer</p>
				{/if}
				<LegendRow
					label={crashesLabel}
					checked={crashesCircleLegend.layerAllEnabled}
					disabled={disableByZoom(crashesHeatmapLegend.minzoom, mapZoom)}
					disabledTooltip="Zoom in to enable this layer"
					onchange={toggleCrashesVisibility}>
					<LegendPatch
						color={crashesCircleLegend.legendItems[0].color ?? PATCH_FALLBACK}
						shape="round" />
				</LegendRow>
			</div>
		{/if}
	</LegendSection>

	{#if crashesAvailable || !uploadNeedsCrashes}
		<LegendSection heading="Upload crash data">
			{#snippet action()}
				<InfoButton
					content="Show your own crash records on the map, colored by severity. Choose an .xlsx or .csv file, or a zipped shapefile (.zip), with crash locations. The file is read in your browser and not sent anywhere." />
			{/snippet}
			<CustomCrashes systemId={ECrashes.UploadedCrashes} />
		</LegendSection>
	{/if}
</div>

<style>
	.detail {
		display: block;
		color: var(--text-muted);
		font-size: var(--font-size-xs);
	}
</style>
